import {loggerFactory} from "@cm/browser-log"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import {PriorityQueue} from "@cm/utils"
import type {Vector2Like} from "@cm/math"
import {Vector2} from "@cm/math"
import {WebGl2ImagePhysical} from "#webgl2/webgl2-image-physical"
import type {AtlasStats} from "@cm/hal"

const CLEAR_VALUE_FOR_FREED_PAGES = undefined // only for debugging purposes

export type FreePageCallback = (pageHandle: PageHandle) => void

export type PageHandle = {
    readonly id: number
    readonly atlasId: number
    readonly pageIndex: Vector2
    freePageCallback?: FreePageCallback
    isLocked: boolean
    priority: number
    lastUsedTime: number
}

export type AtlasDescriptor = {
    id: number
    numPages: number
}

export class WebGl2ImageAtlas {
    readonly id: number

    constructor(
        readonly context: WebGl2Context,
        readonly descriptor: AtlasDescriptor,
    ) {
        this.id = descriptor.id
        const {atlasImage, numPages, stats} = this.createAtlas(descriptor)
        this._atlasImage = atlasImage
        this._numPages = numPages
        this._stats = stats
        this.initCache()
    }

    // HalEntity
    dispose(): void {
        this.releaseAtlas()
    }

    get stats() {
        return this._stats
    }

    get image(): WebGl2ImagePhysical {
        return this._atlasImage
    }

    get numAvailablePages() {
        return this._pageHandlePriorityQueue.size
    }

    get leastRecentlyUsedTime(): number {
        return this._pageHandlePriorityQueue.peek()?.lastUsedTime ?? 0
    }

    private findNewPageByLeastPriority() {
        return this._pageHandlePriorityQueue.peek()
    }

    // private findNewPageStochastically() {
    //     if (this._pageHandlePriorityQueue.peekPriority() === PAGE_PRIORITY_FREE) {
    //         return this._pageHandlePriorityQueue.peek()
    //     } else {
    //         // const a = this._pageHandlePriorityQueue.heap.map((entry) => Math.exp(-entry.priority * 0.00001))
    //         const a = this._pageHandlePriorityQueue.heap.map((entry) => Math.exp(((entry.element.lastUsedTime - this.currTime) / this._stats.numPages) * 1.0))
    //         const totalPriority = a.reduce((acc, curr) => acc + curr, 0)
    //         const scale = 1 / totalPriority
    //         const rand = Math.random()
    //         let cumulative = 0
    //         let index = 0
    //         for (const v of a) {
    //             cumulative += v * scale
    //             if (rand < cumulative) {
    //                 return this._pageHandlePriorityQueue.heap[index].element
    //             }
    //             index++
    //         }
    //         return this._pageHandlePriorityQueue.peek() // fallback, should not happen
    //     }
    // }

    private findNewPage() {
        return this.findNewPageByLeastPriority()
        // return this.findNewPageStochastically()
    }

    addPage(priority: number, freePageCallback: FreePageCallback): PageHandle {
        // find a page to replace
        const pageHandle = this.findNewPage()
        if (!pageHandle) {
            throw new Error("No free pages")
        }
        this.freePage(pageHandle)
        pageHandle.freePageCallback = freePageCallback
        pageHandle.priority = priority
        this.registerPageUsage(pageHandle)
        logger.trace(`Adding page: ${pageHandle.pageIndex.x}, ${pageHandle.pageIndex.y}`)
        this._stats.numUsedPages++
        return pageHandle
    }

    freePage(pageHandle: PageHandle): void {
        if (pageHandle.isLocked) {
            throw new Error("Attempting to free a locked page")
        }
        const callback = pageHandle.freePageCallback
        pageHandle.freePageCallback = undefined
        if (callback) {
            this._stats.numUsedPages--
            logger.trace(`Freeing page: ${pageHandle.pageIndex.x}, ${pageHandle.pageIndex.y}`)
            callback(pageHandle)
            pageHandle.priority = PAGE_PRIORITY_FREE
            pageHandle.lastUsedTime = 0
            this.updatePagePriority(pageHandle)
            if (CLEAR_VALUE_FOR_FREED_PAGES !== undefined) {
                // clear the page data
                this._atlasImage.clear({
                    color: {r: CLEAR_VALUE_FOR_FREED_PAGES, g: CLEAR_VALUE_FOR_FREED_PAGES, b: CLEAR_VALUE_FOR_FREED_PAGES, a: CLEAR_VALUE_FOR_FREED_PAGES},
                    region: {
                        x: pageHandle.pageIndex.x * WebGl2ImageAtlas.pageSizeWithMargin,
                        y: pageHandle.pageIndex.y * WebGl2ImageAtlas.pageSizeWithMargin,
                        width: WebGl2ImageAtlas.pageSizeWithMargin,
                        height: WebGl2ImageAtlas.pageSizeWithMargin,
                    },
                })
            }
        }
    }

    registerPageUsage(pageHandle: PageHandle): void {
        logger.trace(`Registering page usage: ${pageHandle.pageIndex.x}, ${pageHandle.pageIndex.y}`)
        this.currTime++
        pageHandle.lastUsedTime = this.currTime
        this.updatePagePriority(pageHandle)
    }

    private currTime = 0

    setPagePriority(pageHandle: PageHandle, priority: number): void {
        if (pageHandle.priority === priority) {
            return
        }
        logger.trace(`Setting page priority: ${pageHandle.pageIndex.x}, ${pageHandle.pageIndex.y} to ${priority}`)
        pageHandle.priority = priority
        this.updatePagePriority(pageHandle)
    }

    private updatePagePriority(pageHandle: PageHandle) {
        if (pageHandle.isLocked) {
            return // locked pages are not in the queue; it will be updated once it is unlocked
        }
        const queuePriority = this.computeQueuePriority(pageHandle)
        this._pageHandlePriorityQueue.updatePriority(pageHandle, queuePriority)
    }

    private computeQueuePriority(pageHandle: PageHandle): number {
        const queuePriority = pageHandle.priority + pageHandle.lastUsedTime * LAST_USED_TIME_PRIORITY_SCALING
        return -queuePriority // use negative priority as we want the least priority to be dequeued first (and this is a max heap)
    }

    lockPage(pageHandle: PageHandle): void {
        if (pageHandle.isLocked) {
            throw new Error("Attempting to lock a page that is already locked")
        }
        logger.trace(`Locking page: ${pageHandle.pageIndex.x}, ${pageHandle.pageIndex.y}`)
        pageHandle.isLocked = true
        this._pageHandlePriorityQueue.remove(pageHandle) // remove from LRU
    }

    unlockPage(pageHandle: PageHandle): void {
        if (!pageHandle.isLocked) {
            throw new Error("Attempting to unlock a page that is not locked")
        }
        logger.trace(`Unlocking page: ${pageHandle.pageIndex.x}, ${pageHandle.pageIndex.y}`)
        this.registerPageUsage(pageHandle)
        pageHandle.isLocked = false
        this._pageHandlePriorityQueue.enqueue(pageHandle, this.computeQueuePriority(pageHandle)) // add back to LRU
    }

    private initCache(): void {
        this._pageHandlePriorityQueue.clear()
        let id = 0
        for (let y = 0; y < this._numPages.y; y++) {
            for (let x = 0; x < this._numPages.x; x++) {
                const pageHandle: PageHandle = {
                    id: id++,
                    atlasId: this.id,
                    pageIndex: new Vector2(x, y),
                    isLocked: false,
                    priority: PAGE_PRIORITY_FREE,
                    lastUsedTime: 0,
                }
                this._pageHandlePriorityQueue.enqueue(pageHandle, this.computeQueuePriority(pageHandle))
            }
        }
    }

    private createAtlas(descriptor: AtlasDescriptor): {atlasImage: WebGl2ImagePhysical; numPages: Vector2Like; stats: AtlasStats} {
        if (descriptor.numPages <= 0) {
            throw new Error("Atlas must have at least one page")
        }
        const pageSizeWithMargin = WebGl2ImageAtlas.pageSize + WebGl2ImageAtlas.pageMargin
        // const maxPagesX = Math.floor(this.context.maxTextureSize / pageSizeWithMargin)
        // const numPagesX = Math.min(descriptor.numPages, maxPagesX)
        // const numPagesY = Math.ceil(descriptor.numPages / numPagesX)
        const numPagesX = Math.ceil(Math.sqrt(descriptor.numPages))
        const numPagesY = numPagesX
        logger.debug(
            `Creating atlas image with ${numPagesX}x${numPagesY} pages, each of size ${pageSizeWithMargin}x${pageSizeWithMargin} (${WebGl2ImageAtlas.pageMargin} margin)`,
        )
        const atlasImage = new WebGl2ImagePhysical(this.context, {
            width: numPagesX * pageSizeWithMargin,
            height: numPagesY * pageSizeWithMargin,
            channelLayout: "R",
            dataType: "float32",
        })
        return {
            atlasImage: atlasImage,
            numPages: {x: numPagesX, y: numPagesY},
            stats: {
                totalSizeInBytes: atlasImage.stats.videoMemSizeInBytes,
                numPages: numPagesX * numPagesY,
                numUsedPages: 0,
            },
        }
    }

    private releaseAtlas() {
        this._atlasImage.dispose()
    }

    private _atlasImage: WebGl2ImagePhysical
    private _numPages: Vector2Like
    private _pageHandlePriorityQueue = new PriorityQueue<PageHandle>()
    private _stats: AtlasStats

    static readonly pageSizeShift = 8
    static readonly pageSize = 1 << WebGl2ImageAtlas.pageSizeShift
    static readonly pageMargin = 0
    static readonly pageSizeWithMargin = WebGl2ImageAtlas.pageSize + WebGl2ImageAtlas.pageMargin
}

const PAGE_PRIORITY_FREE = 0

const LAST_USED_TIME_PRIORITY_SCALING = 1

const logger = loggerFactory.getLogger("webgl2")
